import React from 'react';
import { Button, Typography } from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';

import { cn } from '~/lib/utils';
import type { ProductFilterOption } from '~/shared/hooks/useProductFilterOptions';

import DropdownButton, {
  type DropdownOption,
  filterButtonActiveClassName
} from '../DropdownButton';

import {
  FilterDropdownSkeleton,
  ProductFiltersSkeleton
} from './FilterBarSkeleton';

interface ProductFilterBarProps {
  products: ProductFilterOption[];
  selectedProduct: string | null;
  onProductChange: (productName: string | null) => void;
  productsLoading?: boolean;
  areaOptions?: DropdownOption[];
  selectedArea?: string | null;
  onAreaSelect?: (option: DropdownOption) => void;
  areasLoading?: boolean;
  showAreaFilter?: boolean;
  showProductFilters?: boolean;
  productFilterDisabled?: boolean;
  secondaryFilters?: React.ReactNode;
  className?: string;
  trailing?: React.ReactNode;
}

export function ProductFilterBar({
  products,
  selectedProduct,
  onProductChange,
  productsLoading = false,
  areaOptions = [],
  selectedArea = null,
  onAreaSelect,
  areasLoading = false,
  showAreaFilter = true,
  showProductFilters = true,
  productFilterDisabled = false,
  secondaryFilters,
  className = '',
  trailing
}: ProductFilterBarProps) {
  const { t } = useTranslate();

  const shouldShowProductFilters =
    showProductFilters &&
    !productFilterDisabled &&
    (productsLoading || products.length > 0);

  const handleProductClick = (productName: string) => {
    if (productFilterDisabled) {
      return;
    }
    onProductChange(selectedProduct === productName ? null : productName);
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 mb-4 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        {shouldShowProductFilters ? (
          productsLoading ? (
            <ProductFiltersSkeleton />
          ) : (
            products.map(product => (
              <Button
                key={product.id}
                variant="secondary"
                size="sm"
                border="default"
                disabled={productFilterDisabled}
                className={cn(
                  selectedProduct === product.name &&
                    filterButtonActiveClassName
                )}
                onClick={() => handleProductClick(product.name)}>
                <Typography variant="metadata-sm-medium" color="default">
                  {product.label}
                </Typography>
              </Button>
            ))
          )
        ) : null}

        {showAreaFilter ? (
          areasLoading ? (
            <FilterDropdownSkeleton />
          ) : (
            <DropdownButton
              text={t('common.filters.areas', 'Areas')}
              options={areaOptions}
              onSelect={onAreaSelect}
              selectedValue={selectedArea}
              size="sm"
            />
          )
        ) : null}

        {secondaryFilters ? (
          <div className="flex flex-wrap items-center gap-2">
            {secondaryFilters}
          </div>
        ) : null}
      </div>

      {trailing ? (
        <div className="flex flex-wrap items-center gap-2">{trailing}</div>
      ) : null}
    </div>
  );
}
